import { isArray } from 'lodash-es'
import { memo } from 'react'

import { Delay } from '@/components/ui/Delay'
import { FadeSlideAnimation } from '@/components/ui/FadeSlideAnimation'
import { cn } from '../../lib/utils'
import { ChatContent, ChatItem } from '../../models/chat'
import type { ChatActionInterface, ChatActionWithValue } from '../../models/insuranceApplication'
import { ChatMessageComponent } from './ChatMessageComponent'

interface ChatActionProps {
  showActionByDefault?: boolean
  action?: ChatActionInterface
  sendMessage: (data: ChatActionWithValue | FormData) => void
  pendingMessages?: ChatItem[]
  hideAskQuestions?: boolean
  orgId?: string
  dotNumber?: string
}

export const ChatAction = memo(function ChatAction({
  action,
  sendMessage,
  showActionByDefault,
  pendingMessages,
  hideAskQuestions,
  orgId,
  dotNumber,
}: ChatActionProps) {
  if (!action?.content) {
    return null
  }

  function shouldDisableLeaving() {
    if (pendingMessages?.some((item) => item.metadata?.service === 'GENERAL_QUERY')) {
      return true
    }
    return false
  }

  function shouldDisableLeavingAnimation(content: ChatContent) {
    if (content && 'props' in content && (content.props as Record<string, any>)?.disableLeavingAnimation) {
      return true
    }
    return false
  }

  const contents = (isArray(action.content) ? action.content : [action.content]).filter(
    (item) =>
      !!item &&
      // Free text is answered in the composer pinned under the chat.
      item.component !== 'TEXTAREA' &&
      !(item.props as Record<string, unknown> | undefined)?.legacyFallback &&
      !(shouldDisableLeavingAnimation(item as ChatContent) && !showActionByDefault)
  )

  return contents.map((item, i) => {
    const needsGap = i < contents.length - 1

    return (
      <div key={item.key || JSON.stringify({ component: item.component, props: item.props })}>
        <Delay
          visible={shouldDisableLeaving() ? true : showActionByDefault}
          initiallyVisible={false}
          delay={300}
          key={item.id || item.clientId || item.dateCreated || i}
        >
          <FadeSlideAnimation>
            <div
              className={cn(
                'mb-4 flex flex-col items-end rounded-lg',
                hideAskQuestions && !needsGap && 'mb-0'
              )}
            >
              <ChatMessageComponent
                content={item}
                handleSubmit={sendMessage}
                orgId={orgId}
                dotNumber={dotNumber}
              />
            </div>
          </FadeSlideAnimation>
        </Delay>
      </div>
    )
  })
})
